import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import healthService from '../../services/healthService';
import classesService from '../../services/classesService';
import { MagnifyingGlass, Plus, Eye, ArrowLeft, ClipboardText } from '@phosphor-icons/react';

const SCREENING_TYPES = ['vision', 'hearing', 'dental', 'bmi', 'general'];
const STATUSES = ['planned', 'in_progress', 'completed', 'cancelled'];

const emptyForm = {
  name: '',
  screening_type: 'vision',
  target_class_id: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  notes: '',
};

function ScreeningCampaignsPage() {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [classes, setClasses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Detail / roster view
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [roster, setRoster] = useState([]);
  const [existingResults, setExistingResults] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [entryByStudent, setEntryByStudent] = useState({});
  const [submittingResults, setSubmittingResults] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsData, classesData] = await Promise.all([
        healthService.listCampaigns(statusFilter ? { status: statusFilter } : {}),
        classesService.listClasses().catch(() => []),
      ]);
      setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
      setClasses(Array.isArray(classesData) ? classesData : classesData?.items || classesData?.classes || []);
    } catch (error) {
      toast.error('Failed to load screening campaigns');
    } finally {
      setLoading(false);
    }
  };

  const getClassName = (classId) => {
    const cls = classes.find((c) => c.id === classId);
    return cls ? cls.name : classId || '-';
  };

  const openCreateModal = () => {
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.screening_type || !formData.start_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData, target_class_id: formData.target_class_id || null, end_date: formData.end_date || null };
      await healthService.createCampaign(payload);
      toast.success('Screening campaign created');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to create screening campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (campaign, status) => {
    try {
      await healthService.updateCampaign(campaign.id, { status });
      toast.success('Campaign status updated');
      loadData();
      if (selectedCampaign?.id === campaign.id) {
        setSelectedCampaign({ ...selectedCampaign, status });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update campaign status');
    }
  };

  const openDetail = async (campaign) => {
    setSelectedCampaign(campaign);
    setEntryByStudent({});
    setDetailLoading(true);
    try {
      const [resultsData, coverageData] = await Promise.all([
        healthService.listScreeningResults(campaign.id),
        healthService.getCampaignCoverage(campaign.id),
      ]);
      setExistingResults(Array.isArray(resultsData) ? resultsData : []);
      setCoverage(coverageData || null);

      if (campaign.target_class_id) {
        const classStudents = await classesService.getClassStudents(campaign.target_class_id);
        const items = Array.isArray(classStudents) ? classStudents : classStudents?.items || [];
        setRoster(items);
      } else {
        setRoster([]);
      }
    } catch (error) {
      toast.error('Failed to load campaign details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedCampaign(null);
    setRoster([]);
    setExistingResults([]);
    setCoverage(null);
    setEntryByStudent({});
  };

  const isScreened = (studentCode) => existingResults.some((r) => r.student_id === studentCode);

  const updateEntry = (studentCode, field, value) => {
    setEntryByStudent((prev) => ({
      ...prev,
      [studentCode]: { ...prev[studentCode], [field]: value },
    }));
  };

  const handleSubmitResults = async () => {
    const results = Object.entries(entryByStudent)
      .filter(([, entry]) => entry?.result_summary?.trim())
      .map(([studentCode, entry]) => ({
        student_id: studentCode,
        result_summary: entry.result_summary.trim(),
        referred_out: !!entry.referred_out,
        referred_to: entry.referred_out ? (entry.referred_to || null) : null,
        notes: entry.notes || null,
      }));

    if (results.length === 0) {
      toast.error('Enter at least one result before submitting');
      return;
    }

    setSubmittingResults(true);
    try {
      await healthService.bulkCreateScreeningResults(selectedCampaign.id, results);
      toast.success(`Recorded ${results.length} screening result(s)`);
      setEntryByStudent({});
      const [resultsData, coverageData] = await Promise.all([
        healthService.listScreeningResults(selectedCampaign.id),
        healthService.getCampaignCoverage(selectedCampaign.id),
      ]);
      setExistingResults(Array.isArray(resultsData) ? resultsData : []);
      setCoverage(coverageData || null);
    } catch (error) {
      toast.error(error.message || 'Failed to submit screening results');
    } finally {
      setSubmittingResults(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading screening campaigns...</div>
      </Layout>
    );
  }

  if (selectedCampaign) {
    return (
      <Layout>
        <div className="page">
          <div className="page-header">
            <div>
              <button className="btn btn-secondary" onClick={closeDetail} style={{ marginBottom: 12 }}>
                <ArrowLeft size={18} /> Back to Campaigns
              </button>
              <h1>{selectedCampaign.name}</h1>
              <p className="text-capitalize">{selectedCampaign.screening_type} screening — {getClassName(selectedCampaign.target_class_id)}</p>
            </div>
            <select
              className="filter-select"
              value={selectedCampaign.status}
              onChange={(e) => handleStatusChange(selectedCampaign, e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
              ))}
            </select>
          </div>

          {coverage && (
            <div className="form-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 24, alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 700 }}>{coverage.screened_count} / {coverage.target_count || '—'}</div>
                <div style={{ color: '#666', fontSize: 13 }}>Students screened</div>
              </div>
              <div style={{ flex: 1, height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${coverage.target_count ? Math.min(100, (coverage.screened_count / coverage.target_count) * 100) : 0}%`,
                    background: '#16a34a',
                  }}
                />
              </div>
            </div>
          )}

          {detailLoading ? (
            <div className="loading">Loading roster...</div>
          ) : !selectedCampaign.target_class_id ? (
            <div className="empty-state">
              <p>This campaign has no target class — no roster to display. Results can still be entered via the API for individual students.</p>
            </div>
          ) : roster.length === 0 ? (
            <div className="empty-state">
              <p>No students found in the target class</p>
            </div>
          ) : (
            <>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Status</th>
                      <th>Result Summary</th>
                      <th>Referred Out</th>
                      <th>Referred To</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roster.map((student) => {
                      const screened = isScreened(student.student_id);
                      const entry = entryByStudent[student.student_id] || {};
                      return (
                        <tr key={student.id}>
                          <td>{student.first_name} {student.last_name}</td>
                          <td>
                            {screened ? (
                              <span className="badge active">Screened</span>
                            ) : (
                              <span className="badge inactive">Pending</span>
                            )}
                          </td>
                          <td>
                            <input
                              type="text"
                              disabled={screened}
                              value={entry.result_summary || ''}
                              onChange={(e) => updateEntry(student.student_id, 'result_summary', e.target.value)}
                              placeholder={screened ? 'Already recorded' : 'e.g., 6/6 vision, normal'}
                              style={{ minWidth: 180 }}
                            />
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              disabled={screened}
                              checked={!!entry.referred_out}
                              onChange={(e) => updateEntry(student.student_id, 'referred_out', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              disabled={screened || !entry.referred_out}
                              value={entry.referred_to || ''}
                              onChange={(e) => updateEntry(student.student_id, 'referred_to', e.target.value)}
                              placeholder="Referral destination"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              disabled={screened}
                              value={entry.notes || ''}
                              onChange={(e) => updateEntry(student.student_id, 'notes', e.target.value)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 16 }}>
                <button className="btn btn-primary" onClick={handleSubmitResults} disabled={submittingResults}>
                  <ClipboardText size={18} />
                  {submittingResults ? 'Submitting...' : 'Submit Results'}
                </button>
              </div>
            </>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Health — Screening Campaigns</h1>
            <p>Vision, hearing, dental, BMI, and general screening drives</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            New Campaign
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <span style={{ color: '#999' }}>Filter by status</span>
          </div>
          <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
            ))}
          </select>
        </div>

        <div className="table-container">
          {campaigns.length === 0 ? (
            <div className="empty-state">
              <p>No screening campaigns found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Target Class</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td>{campaign.name}</td>
                    <td className="text-capitalize">{campaign.screening_type}</td>
                    <td>{getClassName(campaign.target_class_id)}</td>
                    <td>{campaign.start_date}</td>
                    <td>{campaign.end_date || '-'}</td>
                    <td>
                      <select
                        className="filter-select"
                        value={campaign.status}
                        onChange={(e) => handleStatusChange(campaign, e.target.value)}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}</option>
                        ))}
                      </select>
                    </td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="View Roster" onClick={() => openDetail(campaign)}>
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>New Screening Campaign</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Campaign Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Term 2 Vision Screening" required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Screening Type *</label>
                  <select value={formData.screening_type} onChange={(e) => setFormData({ ...formData, screening_type: e.target.value })} required>
                    {SCREENING_TYPES.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Target Class</label>
                  <select value={formData.target_class_id} onChange={(e) => setFormData({ ...formData, target_class_id: e.target.value })}>
                    <option value="">All / Not Class-Specific</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Campaign'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default ScreeningCampaignsPage;
